import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { getTjenester } from "../lib/tjenestekatalogApi";
import { getDisplayName } from "../utils/displayName";
import { SERVICEPAKKE_KEYS } from "../features/checklist/utils";

const KATEGORI = "sykkelservice";
const BOOKING_URL = "https://roykensykkelservice.no/sykkelservice";

// Kun disse tjenestetype-gruppene skal vise beskrivelse/info under hver
// servicepakke ("Motorservice" o.l. holdes uten for å spare plass på plakaten).
const GRUPPER_MED_BESKRIVELSE = ["uten-motor", "el-sykkel"];

// El-sykkel skal vises først på servicepakke-siden, deretter sykkel uten motor.
const PAKKE_REKKEFOLGE = ["el-sykkel", "uten-motor"];

// Store sykler/lastesykkel-varianten har samme beskrivelse som "Generell
// sykkelservice" — vis bare navn og pris for å spare plass på plakaten.
const PAKKER_UTEN_BESKRIVELSE = ["sykkelservice-os"];

const BUCKETS = [
  { key: "gir-brems", label: "Gir & brems", match: ["girservice", "bremseservice"] },
  { key: "hjul-dekk", label: "Hjul & dekk", match: ["dekkSlangeskift", "hjulNav"], notat: "Priser gjelder pr. hjul" },
  { key: "deler", label: "Bytte av deler/tilbehør", match: ["bytteKomponenter"] },
  { key: "el", label: "El-sykkel-spesifikt", match: ["software", "motorservice"] },
  { key: "annet", label: "Annet", match: [] },
];

function isPakke(servicetype) {
  return (servicetype || []).some((t) => SERVICEPAKKE_KEYS.includes(String(t || "").trim().toLowerCase()));
}

function bucketFor(servicetype) {
  const types = servicetype || [];
  for (const b of BUCKETS) {
    if (b.match.length && types.some((t) => b.match.includes(t))) return b.key;
  }
  return "annet";
}

// Strukturerer Hygraph-beskrivelsen til plakaten: punktlisten ("hva som
// inngår"), eventuelle tilleggsnotater etter punktlisten (f.eks. tillegg for
// softwareoppdatering, forbehold om delekostnad), og NB-påminnelser (f.eks.
// batterinøkkel på el-sykkel). Den innledende markedsføringsteksten før
// punktlisten hoppes bevisst over — den tar for mye plass på en fysisk
// plakat og gir lite ekstra info utover det punktlisten allerede viser.
function parseBeskrivelse(beskrivelse) {
  const avsnitt = (beskrivelse || "")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  let bullets = [];
  const notater = [];
  const nb = [];
  let harFunnetPunktliste = false;

  avsnitt.forEach((p) => {
    const linjer = p
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    if (!linjer.length) return;

    const erPunktliste = linjer.every((l) => l.startsWith("- "));
    const erNB = /^nb!?/i.test(linjer[0]);

    if (erPunktliste) {
      bullets = bullets.concat(linjer.map((l) => l.slice(2).trim()));
      harFunnetPunktliste = true;
    } else if (erNB) {
      nb.push(linjer.join(" ").replace(/^nb!?\s*/i, ""));
    } else if (harFunnetPunktliste) {
      // Avsnitt som kommer etter punktlisten er typisk tillegg/forbehold
      // (prisjustering, delekostnad) og tas med som liten tekst.
      notater.push(linjer.join(" "));
    }
  });

  return { bullets, notater, nb };
}

function PlakatHeader({ tittel, oppdatert, sideEtiketter, side }) {
  return (
    <div className="flex items-center justify-between gap-8 pb-8 border-b-4 border-red-600 shrink-0">
      <div className="flex items-center gap-6 min-w-0">
        <img
          src="/images/RoykenSykkelservice_logo_transparent.png"
          alt={getDisplayName()}
          className="h-20 w-auto object-contain shrink-0"
        />
        <div className="w-px h-16 bg-gray-200 shrink-0" />
        <div className="min-w-0">
          <h1 className="text-6xl font-extrabold text-gray-900 tracking-tight">{tittel}</h1>
          <p className="text-xl text-gray-400 mt-1">
            {sideEtiketter[side - 1]} · Oppdatert {oppdatert}
          </p>
        </div>
      </div>
      <div className="flex flex-col items-center shrink-0 bg-blue-600 rounded-3xl px-7 py-6 shadow-lg shadow-blue-600/20">
        <div className="bg-white rounded-2xl p-3">
          <QRCodeSVG value={BOOKING_URL} size={120} level="M" />
        </div>
        <p className="text-sm font-bold text-white mt-3 text-center">Book time online</p>
        <p className="text-[11px] text-blue-100 mt-0.5 text-center">roykensykkelservice.no</p>
      </div>
    </div>
  );
}

function PlakatFooter() {
  return (
    <div className="mt-auto pt-8 shrink-0 text-center">
      <div className="h-px bg-gray-200 mb-6" />
      <p className="text-sm text-gray-400">
        Alle priser er veiledende arbeidspriser inkl. mva. Eventuelle deler kommer i tillegg.
      </p>
      <p className="text-sm text-gray-400 mt-1">roykensykkelservice.no</p>
    </div>
  );
}

function PlakatPrislisteSykkel() {
  const [grupper, setGrupper] = useState(null);
  const [feil, setFeil] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await getTjenester();
        setGrupper(Array.isArray(data) ? data : []);
      } catch {
        setFeil(true);
      }
    })();
  }, []);

  const { pakkegrupper, bucketListe } = useMemo(() => {
    const sykkelgrupper = (grupper || []).filter((g) =>
      (g.service || []).some((s) => s.kategori === KATEGORI)
    );

    // Servicepakker: gruppert per sykkeltype (uten-motor, el-sykkel, ...)
    const pakkegrupper = sykkelgrupper
      .map((g) => ({
        label: g.tjenestetype || g.slug,
        slug: g.slug,
        items: (g.service || [])
          .filter((s) => s.kategori === KATEGORI && isPakke(s.servicetype) && (s.pris || 0) > 0)
          .sort((a, b) => (a.sortering ?? 999) - (b.sortering ?? 999)),
      }))
      .filter((g) => g.items.length > 0)
      .sort((a, b) => {
        const idx = (slug) => {
          const i = PAKKE_REKKEFOLGE.indexOf(slug);
          return i === -1 ? PAKKE_REKKEFOLGE.length : i;
        };
        return idx(a.slug) - idx(b.slug);
      });

    // Enkeltservicer: samlet og dedupliserte på tvers av sykkeltyper, gruppert i kategori-bøtter
    const sett = new Map();
    sykkelgrupper.forEach((g) => {
      (g.service || []).forEach((s) => {
        if (s.kategori !== KATEGORI) return;
        if (isPakke(s.servicetype)) return;
        if (!(s.pris > 0)) return;
        if (!sett.has(s.slug)) sett.set(s.slug, s);
      });
    });

    const bucketListe = BUCKETS.map((b) => ({
      ...b,
      items: [...sett.values()]
        .filter((s) => bucketFor(s.servicetype) === b.key)
        .sort((a, b2) => (a.sortering ?? 999) - (b2.sortering ?? 999)),
    })).filter((b) => b.items.length > 0);

    return { pakkegrupper, bucketListe };
  }, [grupper]);

  const oppdatert = useMemo(
    () => new Date().toLocaleDateString("no-NO", { day: "2-digit", month: "long", year: "numeric" }),
    []
  );

  const sideEtiketter = ["Servicepakker", "Enkeltservicer"];

  return (
    <div className="min-h-screen bg-gray-100 py-8 print:bg-white print:py-0">
      <style>
        {`
@media print {
  @page { size: 297mm 700mm; margin: 0; }
  html, body { margin: 0 !important; padding: 0 !important; background: white !important; }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .plakat-no-print { display: none !important; }
}
`}
      </style>

      <div className="plakat-no-print max-w-3xl mx-auto mb-4 px-4 flex items-center justify-between">
        <Link to="/plakater" className="text-sm text-indigo-600 hover:text-indigo-800">
          ← Tilbake til plakater
        </Link>
        <button
          onClick={() => window.print()}
          className="px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
        >
          Skriv ut prisliste (2 sider)
        </button>
      </div>

      {feil ? (
        <p className="text-center text-gray-400 py-10">Kunne ikke hente prisene.</p>
      ) : grupper === null ? (
        <p className="text-center text-gray-400 py-10">Laster priser…</p>
      ) : (
        <>
          {/* Side 1: Servicepakker */}
          <div className="bg-white w-[297mm] h-[700mm] max-w-full mx-auto print:shadow-none shadow-lg flex flex-col p-16 mb-10 print:mb-0 break-after-page">
            <PlakatHeader tittel="Prisliste" oppdatert={oppdatert} sideEtiketter={sideEtiketter} side={1} />

            <section className="mt-14 shrink-0">
              <div className="flex items-center gap-3 mb-8">
                <span className="w-2.5 h-9 rounded-full bg-blue-600 shrink-0" />
                <h2 className="text-5xl font-bold text-gray-900">Servicepakker</h2>
              </div>
              {pakkegrupper.map((g) => (
                <div key={g.label} className="mb-10">
                  <h3 className="text-lg font-bold text-blue-700 uppercase tracking-wider mb-4">
                    {g.label}
                  </h3>
                  <div className="space-y-5">
                    {g.items.map((item) => {
                      const visBeskrivelse =
                        GRUPPER_MED_BESKRIVELSE.includes(g.slug) &&
                        !PAKKER_UTEN_BESKRIVELSE.includes(item.slug);
                      const { bullets, notater, nb } = visBeskrivelse
                        ? parseBeskrivelse(item.tjenestebeskrivelse)
                        : { bullets: [], notater: [], nb: [] };
                      return (
                        <div
                          key={item.slug}
                          className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
                        >
                          <div className="flex items-center justify-between gap-4 bg-gradient-to-r from-blue-50 to-white px-7 py-5">
                            <span className="text-3xl font-semibold text-gray-900">{item.tjeneste}</span>
                            <span className="text-xl font-bold text-white bg-blue-600 rounded-full px-5 py-2 whitespace-nowrap">
                              {item.pris} kr
                            </span>
                          </div>
                          {(bullets.length > 0 || notater.length > 0 || nb.length > 0) && (
                            <div className="px-7 py-5 bg-white space-y-4">
                              {bullets.length > 0 && (
                                <div>
                                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2.5">
                                    Inkluderer
                                  </p>
                                  <ul className="space-y-2 text-base text-gray-600">
                                    {bullets.map((p, i) => (
                                      <li key={i} className="flex gap-2.5">
                                        <span className="text-blue-500 leading-7 shrink-0">•</span>
                                        <span className="leading-7">{p}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {notater.length > 0 && (
                                <div className="space-y-1">
                                  {notater.map((n, i) => (
                                    <p key={i} className="text-sm text-gray-400 italic leading-6">
                                      {n}
                                    </p>
                                  ))}
                                </div>
                              )}
                              {nb.length > 0 && (
                                <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
                                  <span className="text-xl leading-6 shrink-0">🔑</span>
                                  <p className="text-sm font-semibold text-amber-800 leading-6">
                                    {nb.join(" ")}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </section>

            <PlakatFooter />
          </div>

          {/* Side 2: Enkeltservicer */}
          <div className="bg-white w-[297mm] h-[700mm] max-w-full mx-auto print:shadow-none shadow-lg flex flex-col p-16">
            <PlakatHeader tittel="Prisliste" oppdatert={oppdatert} sideEtiketter={sideEtiketter} side={2} />

            <section className="mt-14 shrink-0">
              <div className="flex items-center gap-3 mb-8">
                <span className="w-2.5 h-9 rounded-full bg-red-600 shrink-0" />
                <h2 className="text-5xl font-bold text-gray-900">Enkeltservicer</h2>
              </div>
              <div className="grid grid-cols-2 gap-6">
                {bucketListe.map((b) => (
                  <div
                    key={b.key}
                    className="rounded-2xl bg-gray-50 border border-gray-100 px-6 py-5"
                  >
                    <h3 className="text-base font-bold text-red-700 uppercase tracking-wider">
                      {b.label}
                    </h3>
                    <p className="text-xs text-gray-400 italic mb-3">{b.notat || " "}</p>
                    <div className="divide-y divide-gray-200">
                      {b.items.map((item) => (
                        <div key={item.slug} className="flex items-center justify-between gap-3 py-2.5">
                          <span className="text-lg text-gray-700">{item.tjeneste}</span>
                          <span className="text-lg font-bold text-gray-900 whitespace-nowrap">
                            {item.pris} kr
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <PlakatFooter />
          </div>
        </>
      )}
    </div>
  );
}

export default PlakatPrislisteSykkel;
